/* src/views/logements/FicheInstructionsCtrl.jsx — L'ONGLET « Instructions »
   de la fiche logement (lot 4, 2026-08-28) : la jauge, les SEPT cartes du
   proto (Caractéristiques · Adresse · Codes d'accès · Wifi et liens ·
   Comment on entre · Parking, poubelles, courses · Les règles), le bloc
   « Ce qui se remplit tout seul » (Assistance / Alentours + « Revoir »),
   la colonne « Ce que reçoit le voyageur » (aperçu message réel), et les
   quatre actions du bas (Vérifier dans un message · Voir le livret ·
   Le QR code · Dépublier).

   ON NE PERD AUCUNE FONCTION : les sections à écriture immédiate sont
   MONTÉES TELLES QUELLES (VilleLogementEditor, TaxeBaremeDetector,
   WifiNetworksEditor, ReglesSejourEditor, CopyToChambresButton,
   VariableInsertPanel, MessagePreviewSection) ; les champs cfg simples sont
   recomposés dans l'idiome du proto avec les MÊMES handlers (double
   écriture horaires plat + schedule, surface refusée si invalide, codes
   optionnels avec confirm au retrait). */

import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { supabase } from '../../supabase'
import { resolveCheckinCheckout } from '../../lib/horaires'
import { parseSurfaceM2Input } from '../../lib/surface-m2'
import { generateTimes, rangeOptions } from '../../tabs/logements/atoms'
import { MessagePreviewSection } from '../../tabs/logements/LivretTab'
import CopyToChambresButton from '../../tabs/logements/CopyToChambresButton'
import VilleLogementEditor from '../../tabs/logements/VilleLogementEditor'
import TaxeBaremeDetector from '../../tabs/logements/TaxeBaremeDetector'
import WifiNetworksEditor from '../../tabs/logements/WifiNetworksEditor'
import ReglesSejourEditor, { hasRegleSejour } from '../../tabs/logements/ReglesSejourEditor'
import VariableInsertPanel from '../../components/admin/VariableInsertPanel'
import LivretQrDownloadButton from '../../tabs/logements/LivretQrDownloadButton'
import Feuille from '../accueil/Feuille'
import { JaugeReglage } from './proto/InfosProto'
import { CarteAuto, ApercuLivretVoyageur, CadreApercuNote } from './proto/LivretProto'
import { useLivretAdminData, joursDepuis, FeuilleDepublierLivret,
  FeuilleRefaireSection } from './livretPartage'

const TIMES = generateTimes()
const PERSONS = rangeOptions(1, 30)
const QUATORZE = [...Array(15).keys()].map((n) => ({ value: n, label: String(n) }))

const noteStyle = { fontSize: '10.5px', color: 'var(--gris)', lineHeight: '1.5',
  margin: '8px 0 0' }

/* Complétude par carte — copie de isCardFilled (LivretTab, présentation pure). */
function carteRemplie(cardId, log, wifiNetworks) {
  const s = (v) => v != null && String(v).trim() !== ''
  switch (cardId) {
    case 'caracteristiques': return s(log.floor)
    case 'adresse': return s(log.adresse_complete)
    case 'codes': return s(log.box_code)
    case 'wifi': return (Array.isArray(wifiNetworks) && wifiNetworks.some((n) => s(n?.ssid))) || s(log.livret_url)
    case 'acces': return s(log.instructions_acces_batiment) || s(log.instructions_acces_appartement)
    case 'vivre': return s(log.parking_info_long) || s(log.infos_poubelles_long) || s(log.infos_courses_stockage)
    case 'regles': return s(log.regles_maison) || hasRegleSejour(log)
    default: return false
  }
}

/* ── une carte d'instructions (accordéon du proto, 58 px) ─────────── */
function CarteInstr({ id, ouvert, onToggle, icone, titre, sousTitre,
  aCompleter, enfants }) {
  return (
    <div style={{ background: '#fff', borderRadius: '14px', marginBottom: '8px',
      overflow: 'hidden',
      boxShadow: ouvert
        ? '0 1px 3px rgba(35,50,64,.08),0 0 0 1px #87b8e6'
        : '0 1px 3px rgba(35,50,64,.08),0 0 0 1px rgba(35,50,64,.05)' }}>
      <button onClick={onToggle} data-testid={`instr-carte-${id}`}
        aria-expanded={ouvert}
        style={{ display: 'flex', alignItems: 'center', gap: '11px', width: '100%',
          textAlign: 'left', background: ouvert ? '#dbeaf7' : '#fff',
          border: 'none', padding: '11px 12px', minHeight: '58px',
          cursor: 'pointer' }}>
        <span style={{ width: '30px', height: '30px', borderRadius: '9px',
          flexShrink: '0', display: 'flex', alignItems: 'center',
          justifyContent: 'center', fontSize: '13px',
          ...(ouvert ? { background: '#87b8e6', color: '#fff' }
            : aCompleter ? { background: 'var(--ambre-bg)', color: 'var(--ambre-tx)' }
              : { background: 'var(--vert-bg)', color: 'var(--vert-tx)' }) }}>{icone}</span>
        <span style={{ flex: '1', minWidth: '0' }}>
          <span style={{ display: 'block', fontSize: '12.5px', fontWeight: '600',
            color: 'var(--navy-d)' }}>{titre}</span>
          <span style={{ display: 'block', fontSize: '10.5px',
            color: aCompleter ? 'var(--ambre-tx)' : 'var(--gris)', marginTop: '1px',
            overflow: 'hidden', textOverflow: 'ellipsis',
            whiteSpace: 'nowrap' }}>{sousTitre}</span>
        </span>
        {aCompleter && (
          <span style={{ width: '7px', height: '7px', borderRadius: '50%',
            background: '#d98c1f', flexShrink: '0' }} />
        )}
        <span style={{ fontSize: '15px', color: 'var(--gris-d)', flexShrink: 0,
          transform: ouvert ? 'rotate(90deg)' : 'rotate(0deg)' }}>›</span>
      </button>
      {ouvert && <div style={{ padding: '11px 12px' }}>{enfants}</div>}
    </div>
  )
}

/* ── un champ du proto (libellé + zone bordée) ────────────────────── */
function ChampInstr({ label, premier = false, children }) {
  return (
    <div style={{ padding: '8px 0',
      borderTop: premier ? 'none' : '1px solid var(--bord)' }}>
      <span style={{ display: 'block', fontSize: '11px', color: 'var(--gris)',
        marginBottom: '5px' }}>{label}</span>
      {children}
    </div>
  )
}

const inpInstr = (manque = false) => ({ width: '100%', border: `1px solid ${manque ? 'var(--ambre-bd)' : '#d5e3ee'}`,
  borderRadius: '9px', padding: '9px 11px', fontSize: '12px',
  color: 'var(--navy-d)', lineHeight: '1.55', outline: 'none', minHeight: '36px',
  fontFamily: 'inherit', background: '#fff', boxSizing: 'border-box' })

function InpInstr({ value, onChange, placeholder = 'non renseigné', testId, type = 'text' }) {
  return (
    <input type={type} value={value ?? ''} data-testid={testId}
      onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
      style={inpInstr(!(value ?? '').toString().trim())} />
  )
}

function TexteInstr({ value, onChange, lignes = 4, placeholder = 'non renseigné', testId }) {
  return (
    <textarea value={value ?? ''} rows={lignes} data-testid={testId}
      onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
      style={{ ...inpInstr(!(value ?? '').trim()), resize: 'vertical' }} />
  )
}

function SelectInstr({ value, onChange, options, testId }) {
  // Les options viennent d'atoms : soit des chaînes brutes (generateTimes),
  // soit des objets { value, label } (rangeOptions) — on normalise.
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)}
      data-testid={testId} style={inpInstr(false)}>
      {options.map((o) => {
        const v = typeof o === 'object' ? o.value : o
        const lb = typeof o === 'object' ? o.label : o
        return <option key={v} value={v}>{lb}</option>
      })}
    </select>
  )
}

/* Codes optionnels (porte / interphone / coffre / garage) — visibilité +
   retrait avec confirm, mécanique de CodesAccesSection conservée. */
const CODES_OPTIONNELS = [
  { key: 'door_code', label: "Code porte d'entrée" },
  { key: 'interphone_code', label: 'Code interphone' },
  { key: 'coffre_code', label: 'Code coffre' },
  { key: 'garage_code', label: 'Code garage' },
]

export default function FicheInstructionsCtrl({ draft, onAllerLivret }) {
  const { selected, c } = draft
  const log = selected.entity
  const sisters = useMemo(() => {
    if (!selected.isChambre || !selected.parentLog) return []
    return (selected.parentLog.chambres || []).filter((ch) => ch.id !== log.id)
  }, [selected, log.id])

  const [ouverte, setOuverte] = useState('caracteristiques')
  const [feuille, setFeuille] = useState(null) // 'apercu-msg'|'apercu-livret'|'depublier'|{regen}
  const [varPanel, setVarPanel] = useState(null) // { champ } — champ cfg ciblé
  const [codesVisibles, setCodesVisibles] = useState(() => new Set(
    CODES_OPTIONNELS.filter((cd) => (log[cd.key] || '').toString().trim()).map((cd) => cd.key)))
  const [menuCodes, setMenuCodes] = useState(false)
  const zonesRef = useRef({}) // champ → textarea (insertion variable au curseur)

  useEffect(() => {
    setOuverte('caracteristiques'); setFeuille(null); setVarPanel(null)
    setCodesVisibles(new Set(
      CODES_OPTIONNELS.filter((cd) => (log[cd.key] || '').toString().trim()).map((cd) => cd.key)))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [log.id])

  // Le livret (config + modules + faqs + sections auto + wifi) — partagé.
  const livret = useLivretAdminData(log.id)
  const wifiNetworks = livret.wifiNetworks

  const basculer = (id) => setOuverte((prev) => (prev === id ? '' : id))

  const updateNested = (parentKey, field, value) => {
    const current = log[parentKey] || {}
    draft.updateLog(parentKey, { ...current, [field]: value })
  }

  // Double écriture horaires (plat autoritaire + miroir schedule), atomique.
  const updateHoraire = (kind, value) => {
    const flatKey = kind === 'checkin' ? 'checkin_time' : 'checkout_time'
    const scheduleKey = kind === 'checkin' ? 'checkin_start' : 'checkout_time'
    draft.updateLogBatch({
      [flatKey]: value,
      schedule: { ...(log.schedule || {}), [scheduleKey]: value },
    })
  }

  const { checkin: checkinAffiche, checkout: checkoutAffiche } = resolveCheckinCheckout(log)
  const capacity = log.capacity || {}

  const cartes = ['caracteristiques', 'adresse', 'codes', 'wifi', 'acces', 'vivre', 'regles']
  const remplies = cartes.filter((id) => carteRemplie(id, log, wifiNetworks)).length

  // Insertion d'une variable {x} au curseur du textarea ciblé (repli : append).
  const insererVariable = useCallback((nomVar) => {
    if (!varPanel) return
    const champ = varPanel.champ
    const zone = zonesRef.current[champ]
    const actuel = log[champ] || ''
    let suivant
    if (zone && typeof zone.selectionStart === 'number') {
      const p = zone.selectionStart
      suivant = actuel.slice(0, p) + `{${nomVar}}` + actuel.slice(zone.selectionEnd)
    } else {
      suivant = actuel + `{${nomVar}}`
    }
    draft.updateLog(champ, suivant)
  }, [varPanel, log, draft])

  const texteAvecVariables = ({ champ, lignes = 4, testId }) => (
    <>
      <textarea value={log[champ] ?? ''} rows={lignes} data-testid={testId}
        ref={(el) => { zonesRef.current[champ] = el }}
        onChange={(e) => draft.updateLog(champ, e.target.value)}
        placeholder="non renseigné"
        style={{ ...inpInstr(!(log[champ] ?? '').trim()), resize: 'vertical' }} />
      <button className="bt bt-s" style={{ marginTop: '6px', minHeight: '30px',
        fontSize: '10.5px', padding: '0 10px' }}
        onClick={() => setVarPanel({ champ })}>📋 Insérer une variable</button>
    </>
  )


  const sousTitreAuto = (type) => {
    const row = livret.sectionsAuto?.[type]
    if (!row || !row.last_generated_at) return 'jamais construit'
    const j = joursDepuis(row.last_generated_at)
    return `revu il y a ${j} jour${j > 1 ? 's' : ''} — certains commerces ont pu fermer`
  }

  return (
    <>
      <JaugeReglage n={remplies} total={cartes.length} />
      <div className="cols2">
        <div>
          {/* ── Caractéristiques ─────────────────────────────────── */}
          <CarteInstr id="caracteristiques" icone="◫" titre="Caractéristiques"
            sousTitre="limite de voyageurs, horaires, composition"
            aCompleter={!carteRemplie('caracteristiques', log, wifiNetworks)}
            ouvert={ouverte === 'caracteristiques'}
            onToggle={() => basculer('caracteristiques')}
            enfants={(
              <>
                <p style={{ fontSize: '10.5px', color: 'var(--gris-d)',
                  margin: '0 0 10px', lineHeight: '1.5' }}>Sert à : la limite du
                  site direct, les horaires des messages et du contrat, la
                  capacité affichée.</p>
                <ChampInstr label="Nombre de personnes" premier>
                  <SelectInstr value={capacity.max_persons ?? 6}
                    testId="instr-max-persons"
                    onChange={(v) => updateNested('capacity', 'max_persons', parseInt(v, 10))}
                    options={PERSONS} />
                </ChampInstr>
                <ChampInstr label="Surface (m²)">
                  <InpInstr value={capacity.surface_m2 ?? ''} testId="instr-surface"
                    onChange={(v) => {
                      const parsed = parseSurfaceM2Input(v)
                      if (parsed.accepted) updateNested('capacity', 'surface_m2', parsed.value)
                    }} placeholder="ex : 14" />
                </ChampInstr>
                <ChampInstr label="Heure d'arrivée">
                  <SelectInstr value={checkinAffiche} testId="instr-checkin"
                    onChange={(v) => updateHoraire('checkin', v)} options={TIMES} />
                </ChampInstr>
                <ChampInstr label="Heure de départ">
                  <SelectInstr value={checkoutAffiche} testId="instr-checkout"
                    onChange={(v) => updateHoraire('checkout', v)} options={TIMES} />
                </ChampInstr>
                <ChampInstr label="Étage">
                  <InpInstr value={log.floor || ''} testId="instr-floor"
                    onChange={(v) => draft.updateLog('floor', v)}
                    placeholder="ex : 2ème étage" />
                </ChampInstr>
                <ChampInstr label="Nombre de chambres">
                  <SelectInstr value={capacity.bedrooms ?? 2}
                    onChange={(v) => updateNested('capacity', 'bedrooms', parseInt(v, 10))}
                    options={QUATORZE} />
                </ChampInstr>
                <ChampInstr label="Nombre de salles de bain">
                  <SelectInstr value={capacity.bathrooms ?? 1}
                    onChange={(v) => updateNested('capacity', 'bathrooms', parseInt(v, 10))}
                    options={QUATORZE} />
                </ChampInstr>
                <CopyToChambresButton sourceLog={log} sisters={sisters}
                  sectionId="caracteristiques"
                  onCopyToChambres={draft.copyToChambres} />
              </>
            )} />

          {/* ── Adresse ──────────────────────────────────────────── */}
          <CarteInstr id="adresse" icone="◈" titre="Adresse"
            sousTitre={log.adresse_complete || 'à renseigner'}
            aCompleter={!carteRemplie('adresse', log, wifiNetworks)}
            ouvert={ouverte === 'adresse'} onToggle={() => basculer('adresse')}
            enfants={(
              <>
                <ChampInstr label="Adresse complète (voyageur)" premier>
                  <InpInstr value={log.adresse_complete || ''} testId="instr-adresse"
                    onChange={(v) => draft.updateLog('adresse_complete', v)} />
                </ChampInstr>
                <ChampInstr label="Précision (étage, accès…)">
                  <TexteInstr value={log.adresse_precision || ''} lignes={2}
                    onChange={(v) => draft.updateLog('adresse_precision', v)} />
                </ChampInstr>
                {/* Ville (save immédiat au blur) + détecteur de barème taxe —
                    composants montés tels quels (LOT TAXE 0.4). */}
                <div style={{ paddingTop: '8px', borderTop: '1px solid var(--bord)' }}>
                  <VilleLogementEditor log={log} isChambre={selected.isChambre}
                    onUpdate={draft.updateLog}
                    onApplyPatchAndSave={draft.applyPatchAndSaveNow} />
                  <TaxeBaremeDetector log={log} cfg={c}
                    onApplyPatchAndSave={draft.applyPatchAndSaveNow} />
                </div>
                <CopyToChambresButton sourceLog={log} sisters={sisters}
                  sectionId="adresse" onCopyToChambres={draft.copyToChambres} />
              </>
            )} />

          {/* ── Codes d'accès ────────────────────────────────────── */}
          <CarteInstr id="codes" icone="⌘" titre="Codes d'accès"
            sousTitre={log.box_code ? `boîte à clés · ${log.box_code}` : 'à renseigner'}
            aCompleter={!carteRemplie('codes', log, wifiNetworks)}
            ouvert={ouverte === 'codes'} onToggle={() => basculer('codes')}
            enfants={(
              <>
                <ChampInstr label="Code boîte à clés" premier>
                  <InpInstr value={log.box_code || ''} testId="instr-box-code"
                    onChange={(v) => draft.updateLog('box_code', v)} />
                </ChampInstr>
                {CODES_OPTIONNELS.filter((cd) => codesVisibles.has(cd.key)).map((cd) => (
                  <ChampInstr key={cd.key} label={cd.label}>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <InpInstr value={log[cd.key] || ''} testId={`instr-${cd.key}`}
                        onChange={(v) => draft.updateLog(cd.key, v)} />
                      <button aria-label={`Retirer ${cd.label}`}
                        onClick={() => {
                          const valeur = (log[cd.key] || '').toString().trim()
                          if (valeur && !window.confirm(`Retirer « ${cd.label} » effacera sa valeur. Continuer ?`)) return
                          if (valeur) draft.updateLog(cd.key, '')
                          setCodesVisibles((prev) => {
                            const next = new Set(prev); next.delete(cd.key); return next
                          })
                        }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer',
                          color: '#b8412b', fontSize: '14px', flexShrink: 0 }}>✕</button>
                    </div>
                  </ChampInstr>
                ))}
                {codesVisibles.size < CODES_OPTIONNELS.length && (
                  <div style={{ paddingTop: '8px', position: 'relative' }}>
                    <button className="bt bt-s" data-testid="instr-ajouter-code"
                      onClick={() => setMenuCodes((v) => !v)}>
                      + Ajouter un code ({CODES_OPTIONNELS.length - codesVisibles.size} disponibles)</button>
                    {menuCodes && (
                      <div style={{ marginTop: '6px', display: 'flex', gap: '6px',
                        flexWrap: 'wrap' }}>
                        {CODES_OPTIONNELS.filter((cd) => !codesVisibles.has(cd.key)).map((cd) => (
                          <button key={cd.key} className="bt bt-s"
                            style={{ fontSize: '10.5px' }}
                            onClick={() => {
                              setCodesVisibles((prev) => new Set(prev).add(cd.key))
                              setMenuCodes(false)
                            }}>{cd.label}</button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                <CopyToChambresButton sourceLog={log} sisters={sisters}
                  sectionId="codes" onCopyToChambres={draft.copyToChambres} />
              </>
            )} />

          {/* ── Wifi et liens ────────────────────────────────────── */}
          <CarteInstr id="wifi" icone="◉" titre="Wifi et liens"
            sousTitre={(Array.isArray(wifiNetworks) && wifiNetworks[0]?.ssid) || 'à renseigner'}
            aCompleter={!carteRemplie('wifi', log, wifiNetworks)}
            ouvert={ouverte === 'wifi'} onToggle={() => basculer('wifi')}
            enfants={(
              <>
                {/* Source unique Wi-Fi = livret_config.wifi_networks (ADR-198)
                    — éditeur monté tel quel (son propre bouton Enregistrer). */}
                <WifiNetworksEditor logementId={log.id}
                  onSaved={livret.recharger} />
                <ChampInstr label="URL livret externe (optionnel)">
                  <InpInstr value={log.livret_url || ''} testId="instr-livret-url"
                    onChange={(v) => draft.updateLog('livret_url', v)}
                    placeholder="https://…" />
                </ChampInstr>
                <ChampInstr label="Lien avis Google">
                  <InpInstr value={log.avis_google_url || ''} testId="instr-avis-url"
                    onChange={(v) => draft.updateLog('avis_google_url', v)}
                    placeholder="https://g.page/…" />
                  {(log.avis_google_url || '') !== '' &&
                    !(log.avis_google_url || '').startsWith('https://') && (
                    <p style={noteStyle}>Ce lien ne commence pas par https:// —
                      vérifie-le (non bloquant).</p>
                  )}
                </ChampInstr>
              </>
            )} />

          {/* ── Comment on entre ─────────────────────────────────── */}
          <CarteInstr id="acces" icone="⇥" titre="Comment on entre"
            sousTitre={(log.instructions_acces_batiment || log.instructions_acces_appartement || 'à renseigner').slice(0, 60)}
            aCompleter={!carteRemplie('acces', log, wifiNetworks)}
            ouvert={ouverte === 'acces'} onToggle={() => basculer('acces')}
            enfants={(
              <>
                <ChampInstr label="Instructions accès bâtiment" premier>
                  {texteAvecVariables({ champ: 'instructions_acces_batiment', lignes: 5, testId: 'instr-acces-batiment' })}
                </ChampInstr>
                <ChampInstr label="Instructions accès appartement">
                  {texteAvecVariables({ champ: 'instructions_acces_appartement', lignes: 5, testId: 'instr-acces-appartement' })}
                </ChampInstr>
              </>
            )} />

          {/* ── Parking, poubelles, courses ──────────────────────── */}
          <CarteInstr id="vivre" icone="☰" titre="Parking, poubelles, courses"
            sousTitre={(log.parking_info_long || 'à renseigner').slice(0, 60)}
            aCompleter={!carteRemplie('vivre', log, wifiNetworks)}
            ouvert={ouverte === 'vivre'} onToggle={() => basculer('vivre')}
            enfants={(
              <>
                <ChampInstr label="Parking (instructions complètes)" premier>
                  {texteAvecVariables({ champ: 'parking_info_long', lignes: 4, testId: 'instr-parking' })}
                </ChampInstr>
                <ChampInstr label="Poubelles (jours de collecte, instructions)">
                  {texteAvecVariables({ champ: 'infos_poubelles_long', lignes: 4, testId: 'instr-poubelles' })}
                </ChampInstr>
                <ChampInstr label="Courses & stockage">
                  {texteAvecVariables({ champ: 'infos_courses_stockage', lignes: 3, testId: 'instr-courses' })}
                </ChampInstr>
              </>
            )} />

          {/* ── Les règles ───────────────────────────────────────── */}
          <CarteInstr id="regles" icone="◐" titre="Les règles"
            sousTitre={(log.regles_maison || 'à renseigner').slice(0, 60)}
            aCompleter={!carteRemplie('regles', log, wifiNetworks)}
            ouvert={ouverte === 'regles'} onToggle={() => basculer('regles')}
            enfants={(
              <>
                <ChampInstr label="Règles de la maison" premier>
                  {texteAvecVariables({ champ: 'regles_maison', lignes: 6, testId: 'instr-regles' })}
                </ChampInstr>
                {/* Animaux / fumeur — save immédiat awaité, jamais de cascade
                    parent→chambre (composant monté tel quel). */}
                <div style={{ paddingTop: '8px', borderTop: '1px solid var(--bord)' }}>
                  <ReglesSejourEditor log={log} onUpdate={draft.updateLog}
                    onApplyPatchAndSave={draft.applyPatchAndSaveNow} />
                </div>
                <CopyToChambresButton sourceLog={log} sisters={sisters}
                  sectionId="regles" onCopyToChambres={draft.copyToChambres} />
              </>
            )} />

          {/* ── Ce qui se remplit tout seul ──────────────────────── */}
          <p className="inter">Ce qui se remplit tout seul</p>
          <div className="carte" style={{ margin: '0' }}>
            {['assistance', 'alentours'].map((type, i) => (
              <div key={type} style={{ display: 'flex', alignItems: 'center',
                gap: '10px', padding: '9px 0',
                borderTop: i > 0 ? '1px solid var(--bord)' : 'none' }}>
                <span style={{ width: '8px', height: '8px', borderRadius: '50%',
                  flexShrink: '0',
                  background: livret.sectionsAuto?.[type]?.last_generated_at
                    ? '#d98c1f' : '#c3cdd6' }} />
                <span style={{ flex: '1', minWidth: '0' }}>
                  <span style={{ display: 'block', fontSize: '12px',
                    fontWeight: '600', color: 'var(--navy-d)' }}>
                    {type === 'assistance' ? 'Assistance et urgences' : 'Aux alentours'}</span>
                  <span style={{ display: 'block', fontSize: '10px',
                    color: 'var(--ambre-tx)', marginTop: '1px' }}>{sousTitreAuto(type)}</span>
                </span>
                <button onClick={() => setFeuille({ regen: type })}
                  data-testid={`instr-revoir-${type}`}
                  style={{ minHeight: '34px', padding: '0 11px', borderRadius: '8px',
                    border: '1px solid #c3d5e4', background: '#fff', fontSize: '11px',
                    color: 'var(--navy)', flexShrink: '0', cursor: 'pointer' }}>Revoir</button>
              </div>
            ))}
          </div>
        </div>

        {/* ── colonne droite : ce que reçoit le voyageur ─────────── */}
        <div className="surOrdi">
          <div style={{ position: 'sticky', top: '14px' }}>
            <p className="inter" style={{ marginTop: '0' }}>Ce que reçoit le voyageur</p>
            <MessagePreviewSection log={log} cfg={c} prop={selected.prop}
              wifiNetworks={wifiNetworks} onVarClick={null} />
            <p className="note">Vraie réservation, vrai gabarit. Si une valeur
              manque, elle apparaît en accolades.</p>
          </div>
        </div>
      </div>

      {/* ── les actions du bas ─────────────────────────────────────── */}
      <div className="act" style={{ marginTop: '12px' }}>
        <button className="bt bt-s surTel" data-testid="instr-verifier-message"
          onClick={() => setFeuille('apercu-msg')}>Vérifier dans un message</button>
        <button className="bt bt-s" data-testid="instr-voir-livret"
          onClick={() => setFeuille('apercu-livret')}>Voir le livret</button>
        <LivretQrDownloadButton slug={livret.config?.slug}
          title={livret.config?.title?.fr || log.nom} logement_id={log.id} />
        {livret.config?.published && (
          <button className="bt bt-s" data-testid="instr-depublier"
            onClick={() => setFeuille('depublier')}>Dépublier</button>
        )}
      </div>

      {/* ── les feuilles ───────────────────────────────────────────── */}
      {varPanel && (
        <VariableInsertPanel log={log} ctx={{ wifiNetworks }}
          onInsert={insererVariable} onClose={() => setVarPanel(null)} />
      )}
      {feuille === 'apercu-msg' && (
        <Feuille titre="Vérifier dans un message" testId="feuille-apercu-message"
          large onFermer={() => setFeuille(null)}
          enfants={(
            <>
              <div className="info">Le message est composé avec <b>une vraie
                réservation</b> et le gabarit réel. Rien n'est envoyé.</div>
              <MessagePreviewSection log={log} cfg={c} prop={selected.prop}
                wifiNetworks={wifiNetworks} onVarClick={null} />
              <div className="act">
                <button className="bt bt-s" onClick={() => setFeuille(null)}>Fermer</button>
              </div>
            </>
          )} />
      )}
      {feuille === 'apercu-livret' && (
        <Feuille titre="Le livret du voyageur" testId="feuille-apercu-livret"
          onFermer={() => setFeuille(null)}
          enfants={(
            <>
              <div className="info">C'est ce que voit le voyageur en scannant le
                QR code.</div>
              <CadreApercuNote />
              <ApercuLivretVoyageur
                titre={livret.config?.title?.fr || log.nom}
                wifi={Array.isArray(wifiNetworks) && wifiNetworks[0]?.ssid
                  ? `${wifiNetworks[0].ssid}${wifiNetworks[0].password ? ` · ${wifiNetworks[0].password}` : ''}`
                  : null}
                modules={livret.modulesAffiches.map((m) => m.title?.fr || '—')}
                alentoursLigne={livret.alentoursLigne}
                nbFaq={livret.faqs.length}
                guideVide={livret.guideVide} />
              <div className="act">
                <button className="bt bt-s" onClick={() => setFeuille(null)}>Fermer</button>
              </div>
            </>
          )} />
      )}
      {feuille === 'depublier' && (
        <FeuilleDepublierLivret
          onDepublier={async () => {
            await livret.setPublished(false)
            setFeuille(null)
          }}
          onFermer={() => setFeuille(null)} />
      )}
      {feuille && feuille.regen && (
        <FeuilleRefaireSection type={feuille.regen} logementId={log.id}
          sectionRow={livret.sectionsAuto?.[feuille.regen]}
          onFini={() => { livret.recharger(); setFeuille(null) }}
          onFermer={() => setFeuille(null)} />
      )}
    </>
  )
}
